/**
 * AdminEmailSettings.js
 *
 * Full email configuration panel for the admin.
 * Tabs: SMTP Config | Account Creation | Business Approval |
 *       Order Confirmation (GST) | Order Confirmation (No GST)
 *
 * Completely separate from SiteSettings — uses /api/email-settings endpoints.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  FiServer, FiSend, FiEye, FiEyeOff, FiSave,
  FiRefreshCw, FiToggleLeft, FiToggleRight,
  FiCode, FiUser, FiBriefcase, FiShoppingBag,
  FiUpload, FiMail,
} from 'react-icons/fi';
import API from '../../utils/api';
import toast from 'react-hot-toast';

// ── Design tokens (match existing admin palette) ─────────────────────────────
const PURPLE = '#7c3aed';
const PURPLE_LIGHT = '#ede9fe';
const BORDER = '#e5e7eb';
const BG_SOFT = '#f9fafb';
const TEXT_MUTED = '#6b7280';
const SUCCESS_COLOR = '#16a34a';
const DANGER_COLOR  = '#dc2626';

// ── Available template variables per email type ──────────────────────────────
const TEMPLATE_VARS = {
  account_creation: [
    { name: '{{logoUrl}}',          desc: 'Main logo URL set in this section' },
    { name: '{{logoWhite}}',        desc: 'White logo URL set in this section' },
    { name: '{{siteUrl}}',          desc: 'Site URL set in this section' },
    { name: '{{companyName}}',      desc: 'Your company name from Site Settings' },
    { name: '{{customerName}}',     desc: 'Full name of the new user' },
    { name: '{{customerEmail}}',    desc: 'Email address of the new user' },
    { name: '{{customerPhone}}',    desc: 'Phone number of the new user' },
    { name: '{{registrationDate}}', desc: 'Date the account was created' },
    { name: '{{supportEmail}}',     desc: 'Support email from Site Settings' },
    { name: '{{supportPhone}}',     desc: 'Support phone from Site Settings' },
    { name: '{{supportAddress}}',   desc: 'Primary address from Site Settings' },
    { name: '{{website}}',          desc: 'Website URL from Site Settings' },
    { name: '{{bannerImage}}',      desc: 'Banner image URL set in this section' },
    { name: '{{year}}',             desc: 'Current year for footer copyright' },
  ],
  business_approval: [
    { name: '{{logoUrl}}',          desc: 'Main logo URL set in this section' },
    { name: '{{logoWhite}}',        desc: 'White logo URL set in this section' },
    { name: '{{companyName}}',      desc: 'Your company name from Site Settings' },
    { name: '{{businessName}}',     desc: 'Approved business company name' },
    { name: '{{registrationDate}}', desc: 'Date the business was registered' },
    { name: '{{contactPerson}}',    desc: 'Business contact person name' },
    { name: '{{contactEmail}}',     desc: 'Business contact person email' },
    { name: '{{contactNumber}}',    desc: 'Business contact person phone' },
    { name: '{{siteUrl}}',          desc: 'Site URL set in this section' },
    { name: '{{supportAddress}}',   desc: 'Primary address from Site Settings' },
    { name: '{{supportEmail}}',     desc: 'Support email from Site Settings' },
    { name: '{{supportPhone}}',     desc: 'Support phone from Site Settings' },
    { name: '{{website}}',          desc: 'Website URL from Site Settings' },
    { name: '{{bannerImage}}',      desc: 'Banner image URL set in this section' },
    { name: '{{year}}',             desc: 'Current year for footer copyright' },
  ],
  order_confirmation_gst: [
    { name: '{{customerName}}',   desc: 'Customer full name' },
    { name: '{{orderNumber}}',    desc: 'Order number' },
    { name: '{{orderDate}}',      desc: 'Date the order was placed' },
    { name: '{{deliveryDate}}',   desc: 'Estimated delivery date' },
    { name: '{{orderItems}}',     desc: 'Full HTML table rows for all items (use inside a <table>)' },
    { name: '{{productName}}',    desc: 'First item product name' },
    { name: '{{productSku}}',     desc: 'First item SKU' },
    { name: '{{quantity}}',       desc: 'First item quantity' },
    { name: '{{unitPrice}}',      desc: 'First item unit price (₹)' },
    { name: '{{gst}}',            desc: 'First item GST percentage' },
    { name: '{{amount}}',         desc: 'First item total amount (₹)' },
    { name: '{{subtotal}}',       desc: 'Order subtotal (₹)' },
    { name: '{{taxableAmount}}',  desc: 'Subtotal minus discount (₹)' },
    { name: '{{gstAmount}}',      desc: 'Total GST amount (₹)' },
    { name: '{{shippingCharges}}', desc: 'Shipping charges (₹)' },
    { name: '{{discount}}',       desc: 'Discount amount (₹)' },
    { name: '{{grandTotal}}',     desc: 'Final grand total (₹)' },
    { name: '{{logoUrl}}',        desc: 'Main logo URL set in this section' },
    { name: '{{logoWhite}}',      desc: 'White logo URL set in this section' },
    { name: '{{companyName}}',    desc: 'Company name from Site Settings' },
    { name: '{{supportAddress}}', desc: 'Primary address from Site Settings' },
    { name: '{{supportEmail}}',   desc: 'Support email from Site Settings' },
    { name: '{{supportPhone}}',   desc: 'Support phone from Site Settings' },
    { name: '{{website}}',        desc: 'Website URL from Site Settings' },
    { name: '{{siteUrl}}',        desc: 'Site URL set in this section' },
    { name: '{{bannerImage}}',    desc: 'Banner image URL set in this section' },
    { name: '{{year}}',           desc: 'Current year for footer copyright' },
  ],
  order_confirmation_no_gst: [
    { name: '{{customerName}}',   desc: 'Customer full name' },
    { name: '{{orderNumber}}',    desc: 'Order number' },
    { name: '{{orderDate}}',      desc: 'Date the order was placed' },
    { name: '{{deliveryDate}}',   desc: 'Estimated delivery date' },
    { name: '{{orderItems}}',     desc: 'Full HTML table rows for all items (use inside a <table>)' },
    { name: '{{productName}}',    desc: 'First item product name' },
    { name: '{{productSku}}',     desc: 'First item SKU' },
    { name: '{{quantity}}',       desc: 'First item quantity' },
    { name: '{{unitPrice}}',      desc: 'First item unit price (₹)' },
    { name: '{{amount}}',         desc: 'First item total amount (₹)' },
    { name: '{{subtotal}}',       desc: 'Order subtotal (₹)' },
    { name: '{{shippingCharges}}', desc: 'Shipping charges (₹)' },
    { name: '{{discount}}',       desc: 'Discount amount (₹)' },
    { name: '{{grandTotal}}',     desc: 'Final grand total (₹)' },
    { name: '{{logoUrl}}',        desc: 'Main logo URL set in this section' },
    { name: '{{logoWhite}}',      desc: 'White logo URL set in this section' },
    { name: '{{companyName}}',    desc: 'Company name from Site Settings' },
    { name: '{{supportAddress}}', desc: 'Primary address from Site Settings' },
    { name: '{{supportEmail}}',   desc: 'Support email from Site Settings' },
    { name: '{{supportPhone}}',   desc: 'Support phone from Site Settings' },
    { name: '{{website}}',        desc: 'Website URL from Site Settings' },
    { name: '{{siteUrl}}',        desc: 'Site URL set in this section' },
    { name: '{{bannerImage}}',    desc: 'Banner image URL set in this section' },
    { name: '{{year}}',           desc: 'Current year for footer copyright' },
  ],
  order_edit_gst: [
    { name: '{{customerName}}',   desc: 'Customer full name' },
    { name: '{{orderNumber}}',    desc: 'Order number' },
    { name: '{{orderDate}}',      desc: 'Original order date' },
    { name: '{{editedDate}}',     desc: 'Date the order was edited' },
    { name: '{{deliveryDate}}',   desc: 'Estimated delivery date' },
    { name: '{{orderItems}}',     desc: 'Full HTML table rows for all items (use inside a <table>)' },
    { name: '{{productName}}',    desc: 'First item product name' },
    { name: '{{productSku}}',     desc: 'First item SKU' },
    { name: '{{quantity}}',       desc: 'First item quantity' },
    { name: '{{unitPrice}}',      desc: 'First item unit price (₹)' },
    { name: '{{gst}}',            desc: 'First item GST percentage' },
    { name: '{{amount}}',         desc: 'First item total amount (₹)' },
    { name: '{{subtotal}}',       desc: 'Order subtotal (₹)' },
    { name: '{{taxableAmount}}',  desc: 'Subtotal minus discount (₹)' },
    { name: '{{gstAmount}}',      desc: 'Total GST amount (₹)' },
    { name: '{{shippingCharges}}', desc: 'Shipping charges (₹)' },
    { name: '{{discount}}',       desc: 'Discount amount (₹)' },
    { name: '{{grandTotal}}',     desc: 'Final grand total (₹)' },
    { name: '{{logoUrl}}',        desc: 'Main logo URL set in this section' },
    { name: '{{logoWhite}}',      desc: 'White logo URL set in this section' },
    { name: '{{companyName}}',    desc: 'Company name from Site Settings' },
    { name: '{{supportAddress}}', desc: 'Primary address from Site Settings' },
    { name: '{{supportEmail}}',   desc: 'Support email from Site Settings' },
    { name: '{{supportPhone}}',   desc: 'Support phone from Site Settings' },
    { name: '{{website}}',        desc: 'Website URL from Site Settings' },
    { name: '{{siteUrl}}',        desc: 'Site URL set in this section' },
    { name: '{{bannerImage}}',    desc: 'Banner image URL set in this section' },
    { name: '{{year}}',           desc: 'Current year for footer copyright' },
  ],
  order_edit_no_gst: [
    { name: '{{customerName}}',   desc: 'Customer full name' },
    { name: '{{orderNumber}}',    desc: 'Order number' },
    { name: '{{orderDate}}',      desc: 'Original order date' },
    { name: '{{editedDate}}',     desc: 'Date the order was edited' },
    { name: '{{deliveryDate}}',   desc: 'Estimated delivery date' },
    { name: '{{orderItems}}',     desc: 'Full HTML table rows for all items (use inside a <table>)' },
    { name: '{{productName}}',    desc: 'First item product name' },
    { name: '{{productSku}}',     desc: 'First item SKU' },
    { name: '{{quantity}}',       desc: 'First item quantity' },
    { name: '{{unitPrice}}',      desc: 'First item unit price (₹)' },
    { name: '{{amount}}',         desc: 'First item total amount (₹)' },
    { name: '{{subtotal}}',       desc: 'Order subtotal (₹)' },
    { name: '{{shippingCharges}}', desc: 'Shipping charges (₹)' },
    { name: '{{discount}}',       desc: 'Discount amount (₹)' },
    { name: '{{grandTotal}}',     desc: 'Final grand total (₹)' },
    { name: '{{logoUrl}}',        desc: 'Main logo URL set in this section' },
    { name: '{{logoWhite}}',      desc: 'White logo URL set in this section' },
    { name: '{{companyName}}',    desc: 'Company name from Site Settings' },
    { name: '{{supportAddress}}', desc: 'Primary address from Site Settings' },
    { name: '{{supportEmail}}',   desc: 'Support email from Site Settings' },
    { name: '{{supportPhone}}',   desc: 'Support phone from Site Settings' },
    { name: '{{website}}',        desc: 'Website URL from Site Settings' },
    { name: '{{siteUrl}}',        desc: 'Site URL set in this section' },
    { name: '{{bannerImage}}',    desc: 'Banner image URL set in this section' },
    { name: '{{year}}',           desc: 'Current year for footer copyright' },
  ],
  order_cancel_gst: [
    { name: '{{customerName}}',   desc: 'Customer full name' },
    { name: '{{orderNumber}}',    desc: 'Order number' },
    { name: '{{orderDate}}',      desc: 'Original order date' },
    { name: '{{cancelledDate}}',  desc: 'Date the order was cancelled' },
    { name: '{{deliveryDate}}',   desc: 'Originally estimated delivery date' },
    { name: '{{orderItems}}',     desc: 'Full HTML table rows for all items (use inside a <table>)' },
    { name: '{{productName}}',    desc: 'First item product name' },
    { name: '{{productSku}}',     desc: 'First item SKU' },
    { name: '{{quantity}}',       desc: 'First item quantity' },
    { name: '{{unitPrice}}',      desc: 'First item unit price (₹)' },
    { name: '{{gst}}',            desc: 'First item GST percentage' },
    { name: '{{amount}}',         desc: 'First item total amount (₹)' },
    { name: '{{subtotal}}',       desc: 'Order subtotal (₹)' },
    { name: '{{taxableAmount}}',  desc: 'Subtotal minus discount (₹)' },
    { name: '{{gstAmount}}',      desc: 'Total GST amount (₹)' },
    { name: '{{shippingCharges}}', desc: 'Shipping charges (₹)' },
    { name: '{{discount}}',       desc: 'Discount amount (₹)' },
    { name: '{{grandTotal}}',     desc: 'Final grand total (₹)' },
    { name: '{{logoUrl}}',        desc: 'Main logo URL set in this section' },
    { name: '{{logoWhite}}',      desc: 'White logo URL set in this section' },
    { name: '{{companyName}}',    desc: 'Company name from Site Settings' },
    { name: '{{supportAddress}}', desc: 'Primary address from Site Settings' },
    { name: '{{supportEmail}}',   desc: 'Support email from Site Settings' },
    { name: '{{supportPhone}}',   desc: 'Support phone from Site Settings' },
    { name: '{{website}}',        desc: 'Website URL from Site Settings' },
    { name: '{{siteUrl}}',        desc: 'Site URL set in this section' },
    { name: '{{bannerImage}}',    desc: 'Banner image URL set in this section' },
    { name: '{{year}}',           desc: 'Current year for footer copyright' },
  ],
  order_cancel_no_gst: [
    { name: '{{customerName}}',   desc: 'Customer full name' },
    { name: '{{orderNumber}}',    desc: 'Order number' },
    { name: '{{orderDate}}',      desc: 'Original order date' },
    { name: '{{cancelledDate}}',  desc: 'Date the order was cancelled' },
    { name: '{{deliveryDate}}',   desc: 'Originally estimated delivery date' },
    { name: '{{orderItems}}',     desc: 'Full HTML table rows for all items (use inside a <table>)' },
    { name: '{{productName}}',    desc: 'First item product name' },
    { name: '{{productSku}}',     desc: 'First item SKU' },
    { name: '{{quantity}}',       desc: 'First item quantity' },
    { name: '{{unitPrice}}',      desc: 'First item unit price (₹)' },
    { name: '{{amount}}',         desc: 'First item total amount (₹)' },
    { name: '{{subtotal}}',       desc: 'Order subtotal (₹)' },
    { name: '{{shippingCharges}}', desc: 'Shipping charges (₹)' },
    { name: '{{discount}}',       desc: 'Discount amount (₹)' },
    { name: '{{grandTotal}}',     desc: 'Final grand total (₹)' },
    { name: '{{logoUrl}}',        desc: 'Main logo URL set in this section' },
    { name: '{{logoWhite}}',      desc: 'White logo URL set in this section' },
    { name: '{{companyName}}',    desc: 'Company name from Site Settings' },
    { name: '{{supportAddress}}', desc: 'Primary address from Site Settings' },
    { name: '{{supportEmail}}',   desc: 'Support email from Site Settings' },
    { name: '{{supportPhone}}',   desc: 'Support phone from Site Settings' },
    { name: '{{website}}',        desc: 'Website URL from Site Settings' },
    { name: '{{siteUrl}}',        desc: 'Site URL set in this section' },
    { name: '{{bannerImage}}',    desc: 'Banner image URL set in this section' },
    { name: '{{year}}',           desc: 'Current year for footer copyright' },
  ],
};

// ── Tiny UI primitives ───────────────────────────────────────────────────────

const Label = ({ children, required }) => (
  <label style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 6, display: 'block' }}>
    {children}{required && <span style={{ color: DANGER_COLOR, marginLeft: 2 }}>*</span>}
  </label>
);

const Input = ({ label, required, hint, ...props }) => (
  <div className="form-group" style={{ marginBottom: 18 }}>
    {label && <Label required={required}>{label}</Label>}
    <input className="form-control" style={{ fontSize: '0.9rem' }} {...props} />
    {hint && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>{hint}</div>}
  </div>
);

const Select = ({ label, hint, children, ...props }) => (
  <div className="form-group" style={{ marginBottom: 18 }}>
    {label && <Label>{label}</Label>}
    <select className="form-control" style={{ fontSize: '0.9rem' }} {...props}>
      {children}
    </select>
    {hint && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>{hint}</div>}
  </div>
);

const Card = ({ children, style }) => (
  <div style={{
    background: '#fff',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-lg)',
    overflow: 'hidden',
    marginBottom: 28,
    ...style,
  }}>
    {children}
  </div>
);

const CardHeader = ({ icon, title, right }) => (
  <div style={{
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '18px 24px',
    borderBottom: '1px solid var(--border)',
    background: 'var(--bg-soft)',
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 700, fontSize: '1rem' }}>
      <span style={{ color: 'var(--primary)' }}>{icon}</span>
      {title}
    </div>
    {right}
  </div>
);

const CardBody = ({ children }) => (
  <div style={{ padding: '24px' }}>{children}</div>
);

/** Purple pill toggle with label */
const Toggle = ({ enabled, onChange, label, hint }) => (
  <div style={{
    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
    background: enabled ? PURPLE_LIGHT : BG_SOFT,
    border: `1px solid ${enabled ? PURPLE : BORDER}`,
    borderRadius: 10,
    padding: '14px 18px',
    marginBottom: 20,
    transition: 'all 0.2s',
  }}>
    <div>
      <div style={{ fontWeight: 700, fontSize: '0.9rem', color: enabled ? PURPLE : '#374151' }}>
        {label}
      </div>
      {hint && <div style={{ fontSize: '0.78rem', color: TEXT_MUTED, marginTop: 3 }}>{hint}</div>}
    </div>
    <button
      onClick={() => onChange(!enabled)}
      style={{
        background: 'none', border: 'none', cursor: 'pointer',
        color: enabled ? PURPLE : TEXT_MUTED,
        flexShrink: 0, marginLeft: 16, padding: 0,
        display: 'flex', alignItems: 'center', gap: 6,
        fontSize: '0.82rem', fontWeight: 600,
      }}
    >
      {enabled
        ? <><FiToggleRight size={26} /><span style={{ color: PURPLE }}>Enabled</span></>
        : <><FiToggleLeft size={26} /><span>Disabled</span></>
      }
    </button>
  </div>
);

/** Returns true if the value is the server-sent mask (bullets / dots) */
const isMaskedPassword = (val) =>
  typeof val === 'string' && val.length > 0 && val.replace(/[•*]/g, '').trim() === '';

/** Password field with show/hide.
 *  When the server returns a masked value (••••••••) we show a placeholder
 *  instead of the bullet characters. Clicking the eye still works and shows
 *  the empty field with a clear "saved – type to change" cue.
 *  On focus the mask is cleared so the user can type a new password freely.
 */
const PasswordInput = ({ label, hint, value, onChange, ...rest }) => {
  const [show, setShow] = useState(false);
  const masked = isMaskedPassword(value);

  const handleFocus = () => {
    // Clear the bullet-char mask so the user can type a new password
    if (masked) onChange({ target: { value: '' } });
  };

  return (
    <div style={{ marginBottom: 16 }}>
      {label && <Label>{label}</Label>}
      <div style={{ position: 'relative' }}>
        <input
          className="form-control"
          type={show ? 'text' : 'password'}
          // When masked, show empty so the input just shows the placeholder
          value={masked ? '' : value}
          onChange={onChange}
          onFocus={handleFocus}
          style={{ fontSize: '0.88rem', borderRadius: 7, paddingRight: 42 }}
          {...rest}
        />
        <button
          type="button"
          onClick={() => setShow(s => !s)}
          style={{
            position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
            background: 'none', border: 'none', cursor: 'pointer', color: TEXT_MUTED, padding: 0,
          }}
        >
          {show ? <FiEyeOff size={16} /> : <FiEye size={16} />}
        </button>
      </div>
      {masked && (
        <div style={{ fontSize: '0.75rem', color: SUCCESS_COLOR, marginTop: 4, fontWeight: 600 }}>
          ✓ Password is saved. Click the field or type to change it.
        </div>
      )}
      {!masked && hint && <div style={{ fontSize: '0.75rem', color: TEXT_MUTED, marginTop: 4 }}>{hint}</div>}
    </div>
  );
};

/** Inline image upload button — posts to existing /api/upload endpoint */
const ImageUploadInput = ({ label, hint, value, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const fileRef = React.useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const res = await API.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data.success) {
        onChange(res.data.url);
        toast.success('Image uploaded!');
      } else {
        toast.error('Upload failed.');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div className="form-group" style={{ marginBottom: 18 }}>
      <Label>{label}</Label>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input
          className="form-control"
          style={{ fontSize: '0.9rem', flex: 1 }}
          type="text"
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          placeholder="Paste URL or upload →"
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            background: uploading ? BG_SOFT : PURPLE,
            color: uploading ? TEXT_MUTED : '#fff',
            border: 'none', borderRadius: 'var(--radius)',
            padding: '8px 14px', fontSize: '0.82rem',
            fontWeight: 600, cursor: uploading ? 'not-allowed' : 'pointer',
            whiteSpace: 'nowrap', flexShrink: 0,
            transition: 'background 0.15s',
          }}
        >
          {uploading
            ? <FiRefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} />
            : <FiUpload size={13} />}
          {uploading ? 'Uploading…' : 'Upload'}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp,image/svg+xml,.svg"
          style={{ display: 'none' }}
          onChange={handleFile}
        />
      </div>
      {value && (
        <img
          src={value}
          alt="preview"
          style={{
            marginTop: 8, height: 40, borderRadius: 4,
            border: `1px solid ${BORDER}`, objectFit: 'contain',
            background: '#f9fafb', padding: 4,
          }}
        />
      )}
      {hint && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>{hint}</div>}
    </div>
  );
};

/** Template variable reference chips */
const VarChips = ({ vars }) => (
  <div style={{ marginBottom: 10 }}>
    <div style={{ fontSize: '0.78rem', color: TEXT_MUTED, marginBottom: 6, fontWeight: 600 }}>
      Available variables — click to copy:
    </div>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {vars.map(v => (
        <button
          key={v.name}
          title={v.desc}
          onClick={() => {
            navigator.clipboard?.writeText(v.name);
            toast.success(`Copied ${v.name}`);
          }}
          style={{
            background: PURPLE_LIGHT, color: PURPLE,
            border: `1px solid ${PURPLE}`,
            borderRadius: 5, padding: '3px 9px', fontSize: '0.78rem',
            cursor: 'pointer', fontFamily: 'monospace', fontWeight: 600,
          }}
        >
          {v.name}
        </button>
      ))}
    </div>
  </div>
);

/** HTML template editor with live preview toggle */
const TemplateEditor = ({ value, onChange, vars }) => {
  const [preview, setPreview] = useState(false);

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <Label>Email HTML Template</Label>
        <button
          onClick={() => setPreview(p => !p)}
          style={{
            background: 'none', border: `1px solid ${BORDER}`, borderRadius: 6,
            padding: '4px 10px', fontSize: '0.78rem', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 5, color: PURPLE, fontWeight: 600,
          }}
        >
          {preview ? <><FiCode size={13} /> Edit HTML</> : <><FiEye size={13} /> Preview</>}
        </button>
      </div>

      {vars && <VarChips vars={vars} />}

      {preview ? (
        <div style={{
          border: `1px solid ${BORDER}`, borderRadius: 7, overflow: 'hidden',
          background: '#fff', minHeight: 320,
        }}>
          <div style={{
            background: BG_SOFT, borderBottom: `1px solid ${BORDER}`,
            padding: '6px 14px', fontSize: '0.75rem', color: TEXT_MUTED,
          }}>
            Preview — variables shown as placeholders
          </div>
          <iframe
            title="email-preview"
            srcDoc={value}
            style={{ width: '100%', height: 360, border: 'none' }}
            sandbox="allow-same-origin"
          />
        </div>
      ) : (
        <textarea
          className="form-control"
          value={value}
          onChange={e => onChange(e.target.value)}
          rows={14}
          style={{
            fontSize: '0.82rem', fontFamily: 'monospace',
            borderRadius: 7, resize: 'vertical',
          }}
          placeholder="Paste your HTML template here. Use {{variableName}} for dynamic content."
        />
      )}
      <div style={{ fontSize: '0.75rem', color: TEXT_MUTED, marginTop: 4 }}>
        Full HTML supported. Use inline styles for best email client compatibility.
      </div>
    </div>
  );
};

/** Save + spinner button */
const SaveBtn = ({ saving, onClick, label = 'Save Changes' }) => (
  <button
    onClick={onClick}
    disabled={saving}
    className="btn btn-primary"
    style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 140 }}
  >
    {saving
      ? <FiRefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} />
      : <FiSave size={14} />}
    {saving ? 'Saving…' : label}
  </button>
);

// ── Test Mail Bar ─────────────────────────────────────────────────────────────

const TestMailBar = ({ sectionKey }) => {
  const [to, setTo]           = useState('');
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!to.trim()) {
      toast.error('Please enter an email address.');
      return;
    }
    setSending(true);
    try {
      const res = await API.post('/email-settings/test-section', {
        sectionKey,
        to: to.trim(),
      });
      toast.success(res.data.message || 'Test email sent!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send test email.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{
      background: BG_SOFT,
      border: `1px solid ${BORDER}`,
      borderRadius: 10,
      padding: '16px 18px',
      marginBottom: 24,
    }}>
      <div style={{ fontWeight: 700, fontSize: '0.88rem', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 7 }}>
        <FiMail size={15} style={{ color: PURPLE }} />
        Send Test Email
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <input
          className="form-control"
          type="email"
          value={to}
          onChange={e => setTo(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSend()}
          placeholder="Enter recipient email address"
          style={{ fontSize: '0.9rem', flex: 1 }}
        />
        <button
          onClick={handleSend}
          disabled={sending}
          style={{
            display: 'flex', alignItems: 'center', gap: 7,
            background: sending ? BG_SOFT : PURPLE,
            color: sending ? TEXT_MUTED : '#fff',
            border: 'none', borderRadius: 'var(--radius)',
            padding: '9px 18px', fontSize: '0.85rem',
            fontWeight: 600, cursor: sending ? 'not-allowed' : 'pointer',
            whiteSpace: 'nowrap', flexShrink: 0, transition: 'background 0.15s',
          }}
        >
          {sending
            ? <FiRefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} />
            : <FiSend size={14} />}
          {sending ? 'Sending…' : 'Send Test'}
        </button>
      </div>
      <div style={{ fontSize: '0.75rem', color: TEXT_MUTED, marginTop: 8 }}>
        Sends a test email with dummy data using your saved template and SMTP config.
        Save your changes before testing.
      </div>
    </div>
  );
};

// ── SMTP Config Panel ────────────────────────────────────────────────────────

const SmtpPanel = ({ mailerKey, label, config, onChange, onSave, saving }) => {
  const [testing, setTesting] = useState(false);

  const f = (field) => config[field] ?? '';
  const set = (field, val) => onChange({ ...config, [field]: val });

  const handleTest = async () => {
    setTesting(true);
    try {
      const res = await API.post('/email-settings/test-smtp', {
        mailerKey,
        smtpConfig: config,
      });
      toast.success(res.data.message || 'Test email sent!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'SMTP test failed.');
    } finally {
      setTesting(false);
    }
  };

  return (
    <Card>
      <CardHeader
        icon={<FiServer size={17} />}
        title={label}
        right={
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={handleTest}
              disabled={testing}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                background: 'none', border: `1px solid ${PURPLE}`,
                color: PURPLE, borderRadius: 7, padding: '6px 14px',
                fontSize: '0.82rem', cursor: 'pointer', fontWeight: 600,
              }}
            >
              {testing
                ? <FiRefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} />
                : <FiSend size={13} />}
              {testing ? 'Sending…' : 'Send Test Mail'}
            </button>
            <SaveBtn saving={saving} onClick={onSave} />
          </div>
        }
      />
      <CardBody>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
          <Input
            label="From Name"
            value={f('fromName')}
            onChange={e => set('fromName', e.target.value)}
            placeholder="Angel Printers"
            hint="Display name shown in recipient's inbox"
          />
          <Input
            label="From Email"
            type="email"
            value={f('fromEmail')}
            onChange={e => set('fromEmail', e.target.value)}
            placeholder="noreply@angelprinters.com"
            hint="Sender email address (must be authorised by SMTP)"
          />
          <Input
            label="SMTP Host"
            required
            value={f('host')}
            onChange={e => set('host', e.target.value)}
            placeholder="smtp.gmail.com"
          />
          <div style={{ marginBottom: 16 }}>
            <Label>Port</Label>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <input
                className="form-control"
                type="number"
                value={f('port')}
                onChange={e => set('port', e.target.value)}
                placeholder="587"
                style={{ fontSize: '0.88rem', borderRadius: 7, width: 100 }}
              />
              <label style={{
                display: 'flex', alignItems: 'center', gap: 8,
                fontSize: '0.85rem', cursor: 'pointer', userSelect: 'none',
                background: config.secure ? PURPLE_LIGHT : BG_SOFT,
                border: `1px solid ${config.secure ? PURPLE : BORDER}`,
                borderRadius: 7, padding: '7px 14px',
              }}>
                <input
                  type="checkbox"
                  checked={!!config.secure}
                  onChange={e => set('secure', e.target.checked)}
                  style={{ accentColor: PURPLE, width: 15, height: 15 }}
                />
                <span style={{ fontWeight: 600, color: config.secure ? PURPLE : '#374151' }}>
                  SSL/TLS (port 465)
                </span>
              </label>
            </div>
            <div style={{ fontSize: '0.75rem', color: TEXT_MUTED, marginTop: 4 }}>
              Use 587 for STARTTLS, 465 for SSL
            </div>
          </div>
          <Input
            label="SMTP Username"
            required
            value={f('user')}
            onChange={e => set('user', e.target.value)}
            placeholder="your@gmail.com"
          />
          <PasswordInput
            label="SMTP Password / App Password"
            value={f('pass')}
            onChange={e => set('pass', e.target.value)}
            placeholder="Enter new password"
            hint="For Gmail, use an App Password, not your account password"
          />
        </div>
      </CardBody>
    </Card>
  );
};

// ── Email Section Panel (Account Creation, Business Approval, Order) ─────────

const EmailSectionPanel = ({ sectionKey, icon, title, config, onChange, onSave, saving }) => {
  const vars = TEMPLATE_VARS[sectionKey];
  const enabled = !!config.enabled;

  return (
    <Card>
      <CardHeader
        icon={icon}
        title={title}
        right={<SaveBtn saving={saving} onClick={onSave} />}
      />
      <CardBody>
        <Toggle
          enabled={enabled}
          onChange={val => onChange({ ...config, enabled: val })}
          label={`${title} emails are ${enabled ? 'ENABLED' : 'DISABLED'}`}
          hint={enabled
            ? 'Emails will be sent automatically when this event occurs.'
            : 'No emails will be sent for this event. Toggle to enable.'}
        />

        <TestMailBar sectionKey={sectionKey} />

        {enabled && (
          <>
            <Select
              label="Send Using"
              value={config.mailer || 'primary'}
              onChange={e => onChange({ ...config, mailer: e.target.value })}
              hint="Choose which SMTP mailer to use for this email type"
            >
              <option value="primary">Mailer 1 (Primary)</option>
              <option value="secondary">Mailer 2 (Secondary)</option>
            </Select>

            <Input
              label="Email Subject"
              value={config.subject || ''}
              onChange={e => onChange({ ...config, subject: e.target.value })}
              placeholder="Email subject line"
              hint={sectionKey.startsWith('order_') ? 'You can use {{orderNumber}} in the subject line.' : undefined}
            />

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
              <ImageUploadInput
                label="Logo URL"
                value={config.logoUrl || ''}
                onChange={val => onChange({ ...config, logoUrl: val })}
                hint="{{logoUrl}} — main logo for email header"
              />
              <ImageUploadInput
                label="White Logo URL"
                value={config.logoWhite || ''}
                onChange={val => onChange({ ...config, logoWhite: val })}
                hint="{{logoWhite}} — white version for dark backgrounds"
              />
            </div>
            <Input
              label="Site URL"
              value={config.siteUrl || ''}
              onChange={e => onChange({ ...config, siteUrl: e.target.value })}
              placeholder="https://angelprinters.com"
              hint="{{siteUrl}} — used for buttons and links in email"
            />
            <ImageUploadInput
              label="Banner Image"
              value={config.bannerImage || ''}
              onChange={val => onChange({ ...config, bannerImage: val })}
              hint="{{bannerImage}} — full width banner shown at bottom of email"
            />

            <TemplateEditor
              value={config.template || ''}
              onChange={val => onChange({ ...config, template: val })}
              vars={vars}
            />
          </>
        )}
      </CardBody>
    </Card>
  );
};

// ── Status indicator for each section in tab bar ─────────────────────────────
const StatusDot = ({ enabled }) => (
  <span style={{
    display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
    background: enabled ? SUCCESS_COLOR : '#d1d5db',
    marginLeft: 6, flexShrink: 0,
  }} />
);

// ── Main Component ────────────────────────────────────────────────────────────

const TABS = [
  { key: 'smtp',                       label: 'SMTP Config',           icon: <FiServer size={14} /> },
  { key: 'account_creation',           label: 'Account Creation',      icon: <FiUser size={14} /> },
  { key: 'business_approval',          label: 'Business Approval',     icon: <FiBriefcase size={14} /> },
  { key: 'order_confirmation_gst',     label: 'Order (With GST)',      icon: <FiShoppingBag size={14} /> },
  { key: 'order_confirmation_no_gst',  label: 'Order (No GST)',        icon: <FiShoppingBag size={14} /> },
  { key: 'order_edit_gst',             label: 'Edit (With GST)',       icon: <FiShoppingBag size={14} /> },
  { key: 'order_edit_no_gst',          label: 'Edit (No GST)',         icon: <FiShoppingBag size={14} /> },
  { key: 'order_cancel_gst',           label: 'Cancel (With GST)',     icon: <FiShoppingBag size={14} /> },
  { key: 'order_cancel_no_gst',        label: 'Cancel (No GST)',       icon: <FiShoppingBag size={14} /> },
];

const AdminEmailSettings = () => {
  const [activeTab, setActiveTab] = useState('smtp');
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState({});

  // All config values live here
  const [smtpPrimary,   setSmtpPrimary]   = useState({});
  const [smtpSecondary, setSmtpSecondary] = useState({});
  const [accountCreation,        setAccountCreation]        = useState({});
  const [businessApproval,       setBusinessApproval]       = useState({});
  const [orderGst,               setOrderGst]               = useState({});
  const [orderNoGst,             setOrderNoGst]             = useState({});
  const [orderEditGst,           setOrderEditGst]           = useState({});
  const [orderEditNoGst,         setOrderEditNoGst]         = useState({});
  const [orderCancelGst,         setOrderCancelGst]         = useState({});
  const [orderCancelNoGst,       setOrderCancelNoGst]       = useState({});



  const values = {
    smtp_primary:              smtpPrimary,
    smtp_secondary:            smtpSecondary,
    account_creation:          accountCreation,
    business_approval:         businessApproval,
    order_confirmation_gst:    orderGst,
    order_confirmation_no_gst: orderNoGst,
    order_edit_gst:            orderEditGst,
    order_edit_no_gst:         orderEditNoGst,
    order_cancel_gst:          orderCancelGst,
    order_cancel_no_gst:       orderCancelNoGst,
  };

  // ── Load all settings on mount ──────────────────────────────────────────
  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await API.get('/email-settings');
      const s = res.data.settings;
      setSmtpPrimary(s.smtp_primary || {});
      setSmtpSecondary(s.smtp_secondary || {});
      setAccountCreation(s.account_creation || {});
      setBusinessApproval(s.business_approval || {});
      setOrderGst(s.order_confirmation_gst || {});
      setOrderNoGst(s.order_confirmation_no_gst || {});
      setOrderEditGst(s.order_edit_gst || {});
      setOrderEditNoGst(s.order_edit_no_gst || {});
      setOrderCancelGst(s.order_cancel_gst || {});
      setOrderCancelNoGst(s.order_cancel_no_gst || {});
    } catch (err) {
      toast.error('Failed to load email settings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── Save one section ────────────────────────────────────────────────────
  const save = async (key) => {
    setSaving(s => ({ ...s, [key]: true }));
    try {
      await API.put(`/email-settings/${key}`, { value: values[key] });
      toast.success('Saved successfully!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed.');
    } finally {
      setSaving(s => ({ ...s, [key]: false }));
    }
  };

  // ── Tab bar ─────────────────────────────────────────────────────────────
  const tabStyle = (key) => ({
    display: 'flex', alignItems: 'center', gap: 7,
    padding: '8px 18px',
    borderRadius: 'var(--radius)',
    border: 'none',
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: 600,
    background: activeTab === key ? '#fff' : 'transparent',
    color: activeTab === key ? 'var(--primary)' : 'var(--text-muted)',
    boxShadow: activeTab === key ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
    whiteSpace: 'nowrap',
    transition: 'all 0.15s',
  });

  const isSectionEnabled = (key) => {
    if (key === 'smtp') return null; // no toggle for SMTP
    return !!values[key]?.enabled;
  };

  if (loading) return (
    <div style={{ padding: 48, textAlign: 'center' }}>
      <div className="spinner" />
      <p style={{ color: 'var(--text-muted)', marginTop: 12 }}>Loading settings…</p>
    </div>
  );

  return (
    <div>
      {/* Page header */}
      <div style={{ marginBottom: 28 }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: 4 }}>Email Settings</h2>        
        {/* <p style={{ color: TEXT_MUTED, marginTop: 6, fontSize: '0.88rem' }}>
          Configure SMTP mailers and manage automated email templates. All changes take effect immediately.
        </p> */}
      </div>

      {/* Info banner
      <div style={{
        background: PURPLE_LIGHT, border: `1px solid ${PURPLE}`,
        borderRadius: 10, padding: '12px 18px',
        display: 'flex', alignItems: 'flex-start', gap: 10,
        marginBottom: 24, fontSize: '0.83rem', color: PURPLE,
      }}>
        <FiCheckCircle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
        <span>
          <strong>Two independent mailers</strong> are available. Configure both under SMTP Config, then choose
          which mailer to use for each email type. Email sends are non-blocking — they never delay order
          placement or registration even if SMTP fails.
        </span>
      </div> */}

      {/* Tab bar */}
      <div className="email-tab-bar" style={{
        display: 'flex', gap: 4,
        background: 'var(--bg-soft)',
        borderRadius: 'var(--radius)',
        padding: 4, marginBottom: 28,
        border: '1px solid var(--border)',
        width: '100%',
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
      }}>
        {TABS.map(t => {
          const enabled = isSectionEnabled(t.key);
          return (
            <button key={t.key} style={tabStyle(t.key)} onClick={() => setActiveTab(t.key)}>
              {t.icon}
              {t.label}
              {enabled !== null && <StatusDot enabled={enabled} />}
            </button>
          );
        })}
      </div>

      {/* Tab content */}

      {/* ── SMTP Config ── */}
      {activeTab === 'smtp' && (
        <>
          <SmtpPanel
            mailerKey="smtp_primary"
            label="Mailer 1 — Primary SMTP"
            config={smtpPrimary}
            onChange={setSmtpPrimary}
            onSave={() => save('smtp_primary')}
            saving={!!saving['smtp_primary']}
          />
          <SmtpPanel
            mailerKey="smtp_secondary"
            label="Mailer 2 — Secondary SMTP"
            config={smtpSecondary}
            onChange={setSmtpSecondary}
            onSave={() => save('smtp_secondary')}
            saving={!!saving['smtp_secondary']}
          />
        </>
      )}

      {/* ── Account Creation ── */}
      {activeTab === 'account_creation' && (
        <EmailSectionPanel
          sectionKey="account_creation"
          icon={<FiUser size={17} />}
          title="Account Creation Email"
          config={accountCreation}
          onChange={setAccountCreation}
          onSave={() => save('account_creation')}
          saving={!!saving['account_creation']}
        />
      )}

      {/* ── Business Approval ── */}
      {activeTab === 'business_approval' && (
        <EmailSectionPanel
          sectionKey="business_approval"
          icon={<FiBriefcase size={17} />}
          title="Business Approval Email"
          config={businessApproval}
          onChange={setBusinessApproval}
          onSave={() => save('business_approval')}
          saving={!!saving['business_approval']}
        />
      )}

      {/* ── Order Confirmation (GST) ── */}
      {activeTab === 'order_confirmation_gst' && (
        <EmailSectionPanel
          sectionKey="order_confirmation_gst"
          icon={<FiShoppingBag size={17} />}
          title="Order Confirmation — With GST"
          config={orderGst}
          onChange={setOrderGst}
          onSave={() => save('order_confirmation_gst')}
          saving={!!saving['order_confirmation_gst']}
        />
      )}

      {/* ── Order Confirmation (No GST) ── */}
      {activeTab === 'order_confirmation_no_gst' && (
        <EmailSectionPanel
          sectionKey="order_confirmation_no_gst"
          icon={<FiShoppingBag size={17} />}
          title="Order Confirmation — Without GST"
          config={orderNoGst}
          onChange={setOrderNoGst}
          onSave={() => save('order_confirmation_no_gst')}
          saving={!!saving['order_confirmation_no_gst']}
        />
      )}

      {/* ── Order Edit (GST) ── */}
      {activeTab === 'order_edit_gst' && (
        <EmailSectionPanel
          sectionKey="order_edit_gst"
          icon={<FiShoppingBag size={17} />}
          title="Order Edited — With GST"
          config={orderEditGst}
          onChange={setOrderEditGst}
          onSave={() => save('order_edit_gst')}
          saving={!!saving['order_edit_gst']}
        />
      )}

      {/* ── Order Edit (No GST) ── */}
      {activeTab === 'order_edit_no_gst' && (
        <EmailSectionPanel
          sectionKey="order_edit_no_gst"
          icon={<FiShoppingBag size={17} />}
          title="Order Edited — Without GST"
          config={orderEditNoGst}
          onChange={setOrderEditNoGst}
          onSave={() => save('order_edit_no_gst')}
          saving={!!saving['order_edit_no_gst']}
        />
      )}

      {/* ── Order Cancel (GST) ── */}
      {activeTab === 'order_cancel_gst' && (
        <EmailSectionPanel
          sectionKey="order_cancel_gst"
          icon={<FiShoppingBag size={17} />}
          title="Order Cancelled — With GST"
          config={orderCancelGst}
          onChange={setOrderCancelGst}
          onSave={() => save('order_cancel_gst')}
          saving={!!saving['order_cancel_gst']}
        />
      )}

      {/* ── Order Cancel (No GST) ── */}
      {activeTab === 'order_cancel_no_gst' && (
        <EmailSectionPanel
          sectionKey="order_cancel_no_gst"
          icon={<FiShoppingBag size={17} />}
          title="Order Cancelled — Without GST"
          config={orderCancelNoGst}
          onChange={setOrderCancelNoGst}
          onSave={() => save('order_cancel_no_gst')}
          saving={!!saving['order_cancel_no_gst']}
        />
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .email-tab-bar::-webkit-scrollbar { display: none; }
      `}</style>
    </div>
  );
};

export default AdminEmailSettings;